import React from 'react';
import {Provider, useSelector} from 'react-redux';
import {BrowserRouter, Route, Routes} from 'react-router-dom';

import {useAuth0} from '@auth0/auth0-react';
import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {selectAccessToken} from 'src/modules/main/services/user-info-slice';
import {store} from 'src/store';

import {
    HorizontalPositionElement,
    PushNotification,
    VerticalPositionElement,
} from '@pb/kit-component-push-notification';

import {BackgroundTaskContextProvider} from './contexts/background-task-context';
import {LocaleContextProvider} from './contexts/locale-context';
import {AuthenticationGateway} from './modules/authentication';
import {DraftPostponeHandler} from './modules/draft-postpone';
import {LoginLinkHandler} from './modules/login-link';
import {Main} from './modules/main';
import {Mixpanel} from './modules/mixpanel';

// eslint-disable-next-line no-restricted-syntax
import './app.component.styl';

const CurrentUserBootstrap: React.FC<{children: React.ReactNode}> = ({children}) => {
    const {isAuthenticated} = useAuth0();
    const accessToken = useSelector(selectAccessToken);
    useCurrentUser({
        autoLoad: true,
        enabled: isAuthenticated && Boolean(accessToken),
    });

    return <>{children}</>;
};

const AuthenticatedApp: React.FC = () => (
    <AuthenticationGateway>
        <LocaleContextProvider>
            <PushNotification anchorOrigin={{
                horizontal: HorizontalPositionElement.LEFT,
                vertical: VerticalPositionElement.BOTTOM,
            }}>
                <BackgroundTaskContextProvider>
                    <CurrentUserBootstrap>
                        <Mixpanel/>
                        <Main/>
                    </CurrentUserBootstrap>
                </BackgroundTaskContextProvider>
            </PushNotification>
        </LocaleContextProvider>
    </AuthenticationGateway>
);

export const App: React.FC = () => {
    return (
        <Provider store={store}>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<LoginLinkHandler/>}/>
                    <Route path="/*" element={<AuthenticatedApp/>}/>
                    <Route path="/sso/draft/postpone" element={<DraftPostponeHandler/>}/>
                </Routes>
            </BrowserRouter>
        </Provider>
    );
};
